// src/components/MsgNotifyListener.jsx
import React, { useEffect, useMemo } from "react";
import { api } from "../api";
import { useSocket } from "../ws/SocketProvider";
import { useNotify } from "../context/notifyStore";
import { resolveCurrentUserId } from "../utils/currentUserId";

const toInt = (v) => {
  if (typeof v === "bigint") return Number(v);
  const n = parseInt(String(v ?? "").trim(), 10);
  return Number.isFinite(n) ? n : null;
};

const withUid = (uid) => ({ headers: { "x-user-id": uid } });

// calcula total de não lidas a partir da lista de threads
const calcTotalUnread = (data) => {
  const arr = Array.isArray(data) ? data : [];
  return arr.reduce((sum, t) => {
    const last = toInt(t.last_msg_id) || 0;
    const lastRead = toInt(t.last_read_message_id) || 0;
    const unread =
      typeof t.unread_count === "number"
        ? t.unread_count
        : Math.max(0, last - lastRead);
    return sum + (unread > 0 ? unread : 0);
  }, 0);
};

export default function MsgNotifyListener() {
  const { socket } = useSocket();
  const { setBadge, clearBadge, pushAviso, incBadge } = useNotify();
  //                                      ^^^^^^^
  const userId = useMemo(() => resolveCurrentUserId(), []);

  // carga inicial + sanity check periódico de não lidas
  useEffect(() => {
    if (!userId) return;

    let cancelled = false;

    const syncUnread = async () => {
      try {
        const r = await api.get("/ssw/msg/threads", {
          params: { limit: 200 },
          ...withUid(userId),
        });
        if (cancelled) return;

        const total = calcTotalUnread(r.data);
        console.log("[MSG][syncUnread] total=", total);

        if (total > 0) setBadge("mensagens", total);
        else clearBadge("mensagens");
      } catch (e) {
        console.warn("[MSG][syncUnread] erro:", e?.message || e);
      }
    };

    // primeira carga
    syncUnread();

    // sanity check a cada 60s
    const timer = setInterval(syncUnread, 60000);

    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [userId, setBadge, clearBadge]);

  // integração com socket: criar avisos + forçar re-sync de não lidas
  useEffect(() => {
    if (!socket || !userId) return;

    let cancelled = false;

    const syncUnread = async () => {
      try {
        const r = await api.get("/ssw/msg/threads", {
          params: { limit: 200 },
          ...withUid(userId),
        });
        if (cancelled) return;

        const total = calcTotalUnread(r.data);
        console.log("[MSG][socket-sync] total=", total);

        if (total > 0) setBadge("mensagens", total);
        else clearBadge("mensagens");
      } catch (e) {
        console.warn("[MSG][socket-sync] erro:", e?.message || e);
      }
    };

    const handleNew = (payload = {}) => {
      const { threadId, message, preview } = payload;
      if (!threadId || !message) return;

      const autorId = toInt(message.autor_id);
      if (autorId && autorId === toInt(userId)) return; // ignora próprio envio

      const autorNome =
        message.autor_nome ||
        message.autor ||
        message.autorName ||
        `#${message.autor_id || "?"}`;

      const tituloThread =
        (preview && (preview.resolved_title || preview.titulo)) || "";

      const textoCurto = String(message.body || "").slice(0, 140);

      // aviso para o Sidebar (mini-alert-center de Comunicação Interna)
      pushAviso({
        tipo: "MSG_NOTIFY",
        threadId,
        msgId: toInt(message.id),
        titulo: tituloThread || `Nova mensagem de ${autorNome}`,
        texto: textoCurto,
        createdAt: message.created_at || new Date().toISOString(),
        lido: false,
      });

      // incrementa badge imediatamente
      incBadge("mensagens");

      // e ainda força recálculo global de não lidas
      syncUnread();
    };

    const handleInThread = (payload = {}) => {
      const { message } = payload;
      const autorId = toInt(message?.autor_id);
      if (autorId && autorId === toInt(userId)) return;
      // aqui não gera aviso, só re-sincroniza contadores
      syncUnread();
    };

    socket.on("msg:new", handleNew);
    socket.on("msg:new:in-thread", handleInThread);

    return () => {
      cancelled = true;
      socket.off("msg:new", handleNew);
      socket.off("msg:new:in-thread", handleInThread);
    };
  }, [socket, userId, setBadge, clearBadge, pushAviso, incBadge]);

  return null;
}
